import React, { useEffect, useState } from 'react'
import { LoadingButton } from '@mui/lab'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import type { TFunction } from 'i18next'
import { ClipboardPaste, GripVertical, Plus, Trash } from 'lucide-react'
import {
  Controller,
  FieldErrors,
  FormProvider,
  useFieldArray,
  useForm,
  useFormContext,
  useWatch,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import type { ExternalMetadataConfigOutput } from '@core/api/generated'
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { restrictToVerticalAxis, restrictToWindowEdges } from '@dnd-kit/modifiers'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { zodResolver } from '@hookform/resolvers/zod'

const MAX_LENGTHS = {
  configName: 100,
  title: 100,
  placeholder: 200,
  optionText: 200,
  optionValue: 200,
} as const

const createConfigFormSchema = (t: TFunction<'common', 'admin.external_metadata'>) => {
  const optionItemSchema = z.object({
    text: z.string().min(1, t('text_required_error')).max(MAX_LENGTHS.optionText),
    value: z.string().min(1, t('value_required_error')).max(MAX_LENGTHS.optionValue),
  })

  const optionsSettingSchema = z
    .object({
      type: z.literal('options'),
      name: z.string().optional(),
      title: z.string().min(1, t('title_required_error')).max(MAX_LENGTHS.title),
      options: z.array(optionItemSchema).min(1, t('option_required_error')),
      placeholder: z.string().max(MAX_LENGTHS.placeholder),
      multiple_options: z.boolean(),
    })
    .refine(
      (data) => {
        const values = data.options.map((opt) => opt.value)
        return new Set(values).size === values.length
      },
      { message: t('duplicate_option_values_error'), path: ['options'] },
    )

  const textSettingSchema = z.object({
    type: z.literal('text'),
    name: z.string().optional(),
    title: z.string().min(1, t('title_required_error')).max(MAX_LENGTHS.title),
    placeholder: z.string().max(MAX_LENGTHS.placeholder),
    max_length: z.number().min(1).max(500),
  })

  return z.object({
    name: z.string().min(1, t('config_name_required_error')).max(MAX_LENGTHS.configName),
    settings: z.array(z.discriminatedUnion('type', [optionsSettingSchema, textSettingSchema])),
  })
}

export type ConfigFormValues = z.infer<ReturnType<typeof createConfigFormSchema>>

type SettingType = 'options' | 'text'

const getDefaultSetting = (type: SettingType): ConfigFormValues['settings'][number] => {
  if (type === 'options') {
    return {
      type: 'options',
      title: '',
      options: [{ text: '', value: '' }],
      placeholder: '',
      multiple_options: false,
    }
  }
  return {
    type: 'text',
    title: '',
    placeholder: '',
    max_length: 20,
  }
}

const normalizeSettings = (
  settings: ExternalMetadataConfigOutput['settings'],
): ConfigFormValues['settings'] => {
  return settings.map((setting) => {
    if (setting.type === 'options') {
      return {
        type: 'options' as const,
        name: setting.name,
        title: setting.title,
        options: setting.options,
        placeholder: setting.placeholder ?? '',
        multiple_options: setting.multiple_options ?? false,
      }
    }
    return {
      type: 'text' as const,
      name: setting.name,
      title: setting.title,
      placeholder: setting.placeholder ?? '',
      max_length: setting.max_length ?? 20,
    }
  })
}

// Centralized styles
const styles = {
  textField: {
    '& .MuiInputBase-root': {
      bgcolor: 'grey.100',
      borderRadius: 1,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '& .MuiInputBase-root.Mui-focused': {
      bgcolor: 'background.paper',
      '& .MuiOutlinedInput-notchedOutline': {
        border: '2px solid',
        borderColor: 'primary.main',
      },
    },
  },
  dragHandle: {
    cursor: 'grab',
    display: 'flex',
    alignItems: 'center',
    p: 0.5,
    borderRadius: 1,
    '&:hover': { bgcolor: 'action.hover' },
  },
}

type OptionRowProps = {
  settingIndex: number
  optionIndex: number
  onRemove: () => void
  disabled?: boolean
  canDelete: boolean
  dragHandleProps?: Record<string, unknown>
  isDragging?: boolean
}

const OptionRow = ({
  settingIndex,
  optionIndex,
  onRemove,
  disabled,
  canDelete,
  dragHandleProps,
  isDragging,
}: OptionRowProps) => {
  const { t } = useTranslation('common', { keyPrefix: 'admin.external_metadata' })
  const { control, setValue } = useFormContext<ConfigFormValues>()

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1,
        mb: 1,
        alignItems: 'center',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        p: 1,
        bgcolor: isDragging ? 'action.hover' : 'background.paper',
        opacity: isDragging ? 0.8 : 1,
      }}
    >
      <Box {...dragHandleProps} sx={{ ...styles.dragHandle, color: 'text.secondary' }}>
        <GripVertical size={16} />
      </Box>
      <Controller
        // Dynamic path cast - safe due to useFieldArray typing
        name={`settings.${settingIndex}.options.${optionIndex}.text` as `settings.0.options.0.text`}
        control={control}
        render={({ field: textField, fieldState }) => (
          <TextField
            {...textField}
            onChange={(e) => {
              textField.onChange(e)
              // Auto-populate value field with same text
              setValue(
                `settings.${settingIndex}.options.${optionIndex}.value` as `settings.0.options.0.value`,
                e.target.value,
              )
            }}
            size="small"
            placeholder={t('option_text_placeholder')}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            disabled={disabled}
            inputProps={{ maxLength: MAX_LENGTHS.optionText }}
            sx={{ flex: 1, ...styles.textField }}
          />
        )}
      />
      <IconButton size="small" onClick={onRemove} disabled={disabled || !canDelete}>
        <Trash size={16} />
      </IconButton>
    </Box>
  )
}

type SortableOptionRowProps = {
  id: string
  children: React.ReactElement
}

const SortableOptionRow = ({ id, children }: SortableOptionRowProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  })

  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
    zIndex: isDragging ? 1 : undefined,
    position: isDragging ? 'relative' : undefined,
  }

  return (
    <Box ref={setNodeRef} style={style}>
      {React.cloneElement(children, {
        dragHandleProps: { ...attributes, ...listeners },
        isDragging,
      })}
    </Box>
  )
}

type OptionsEditorProps = {
  settingIndex: number
  disabled?: boolean
}

const parseCsvToOptions = (csvText: string): Array<{ text: string; value: string }> => {
  return csvText
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .map((line) => {
      const commaIndex = line.indexOf(',')
      if (commaIndex !== -1) {
        const text = line.substring(0, commaIndex).trim()
        const value = line.substring(commaIndex + 1).trim()
        return { text, value: value || text }
      }
      return { text: line, value: line }
    })
    .filter((option) => option.text.length > 0)
}

const OptionsEditor = ({ settingIndex, disabled }: OptionsEditorProps) => {
  const { t } = useTranslation('common', { keyPrefix: 'admin.external_metadata' })
  const { control, trigger, formState } = useFormContext<ConfigFormValues>()
  const { isSubmitted } = formState

  type ArrayFieldError = FieldErrors & { root?: { message?: string }; message?: string }
  const settingErrors = formState.errors.settings?.[settingIndex]
  const optionsErrors = (
    settingErrors && 'options' in settingErrors ? settingErrors.options : null
  ) as ArrayFieldError | null
  const optionsErrorMessage = optionsErrors?.root?.message ?? optionsErrors?.message
  const { t: tCommon } = useTranslation('common')
  const [csvDialogOpen, setCsvDialogOpen] = useState(false)
  const [csvText, setCsvText] = useState('')

  const { fields, append, remove, move } = useFieldArray({
    control,
    name: `settings.${settingIndex}.options` as const,
  })

  const watchedOptions = useWatch({
    control,
    name: `settings.${settingIndex}.options` as `settings.0.options`,
  })

  // Auto-trigger validation when options change (after form has been submitted once)
  useEffect(() => {
    if (isSubmitted) {
      trigger(`settings.${settingIndex}` as const)
    }
  }, [watchedOptions, isSubmitted, trigger, settingIndex])

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const oldIndex = fields.findIndex((f) => f.id === active.id)
    const newIndex = fields.findIndex((f) => f.id === over.id)

    if (oldIndex !== -1 && newIndex !== -1) {
      move(oldIndex, newIndex)
    }
  }

  const handleCsvImport = () => {
    const options = parseCsvToOptions(csvText)
    const lastIndex = fields.length - 1
    if (lastIndex >= 0 && watchedOptions) {
      const lastOption = watchedOptions[lastIndex]
      if (lastOption && !lastOption.text && !lastOption.value) {
        remove(lastIndex)
      }
    }
    options.forEach((option) => append(option))
    setCsvText('')
    setCsvDialogOpen(false)
  }
  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          modifiers={[restrictToVerticalAxis, restrictToWindowEdges]}
        >
          <SortableContext items={fields.map((f) => f.id)} strategy={verticalListSortingStrategy}>
            {fields.map((field, optionIndex) => (
              <SortableOptionRow key={field.id} id={field.id}>
                <OptionRow
                  settingIndex={settingIndex}
                  optionIndex={optionIndex}
                  onRemove={() => remove(optionIndex)}
                  disabled={disabled}
                  canDelete={fields.length > 1}
                />
              </SortableOptionRow>
            ))}
          </SortableContext>
        </DndContext>
      </Box>
      {optionsErrorMessage && (
        <Typography color="error" variant="caption" sx={{ mt: 0.5, display: 'block' }}>
          {optionsErrorMessage}
        </Typography>
      )}
      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
        <Button
          size="small"
          startIcon={<Plus size={16} />}
          onClick={() => append({ text: '', value: '' })}
          disabled={disabled}
        >
          {t('add_option_button')}
        </Button>
        <Button
          size="small"
          startIcon={<ClipboardPaste size={16} />}
          onClick={() => setCsvDialogOpen(true)}
          disabled={disabled}
        >
          {t('paste_csv_button')}
        </Button>
      </Box>

      <Dialog open={csvDialogOpen} onClose={() => setCsvDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('import_options_csv_dialog_title')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t('import_options_csv_instructions')}
          </DialogContentText>
          <TextField
            autoFocus
            multiline
            rows={8}
            fullWidth
            placeholder={`Option A\nOption B\nOption C`}
            value={csvText}
            onChange={(e) => setCsvText(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCsvDialogOpen(false)}>{tCommon('actions.cancel')}</Button>
          <Button
            onClick={handleCsvImport}
            variant="contained"
            disabled={csvText.trim().length === 0}
          >
            {t('import_button')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

type SettingRowProps = {
  index: number
  onRemove: () => void
  disabled?: boolean
  dragHandleProps?: Record<string, unknown>
  isDragging?: boolean
}

const SettingRow = ({
  index,
  onRemove,
  disabled,
  dragHandleProps,
  isDragging,
}: SettingRowProps) => {
  const { t } = useTranslation('common', { keyPrefix: 'admin.external_metadata' })
  const { control } = useFormContext<ConfigFormValues>()
  const settingType = useWatch({ control, name: `settings.${index}.type` })

  const typeLabel = settingType === 'options' ? t('options_field_label') : t('text_field_label')

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        mb: 3,
        bgcolor: isDragging ? 'action.hover' : 'background.paper',
        opacity: isDragging ? 0.8 : 1,
      }}
    >
      {/* Header: Drag handle + Type label + Delete button */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box {...dragHandleProps} sx={styles.dragHandle}>
            <GripVertical size={20} color="gray" />
          </Box>
          <Typography variant="caption" color="text.secondary" fontWeight="medium">
            {typeLabel}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onRemove} disabled={disabled}>
          <Trash size={16} />
        </IconButton>
      </Box>

      {/* Title field */}
      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
        {t('title_label')}
      </Typography>
      <Controller
        name={`settings.${index}.title`}
        control={control}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            fullWidth
            size="small"
            placeholder={t('enter_field_title_placeholder')}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            disabled={disabled}
            inputProps={{ maxLength: MAX_LENGTHS.title }}
            sx={{ mb: 2, ...styles.textField }}
          />
        )}
      />

      {/* Placeholder field */}
      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
        {t('placeholder_label_optional')}
      </Typography>
      <Controller
        name={`settings.${index}.placeholder`}
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            fullWidth
            size="small"
            placeholder={t('placeholder_text_helper')}
            disabled={disabled}
            inputProps={{ maxLength: MAX_LENGTHS.placeholder }}
            sx={{ mb: 2, ...styles.textField }}
          />
        )}
      />

      {settingType === 'options' && (
        <>
          {/* Multi select toggle - right aligned */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
            <Controller
              // Dynamic path cast - safe since we check settingType === 'options'
              name={`settings.${index}.multiple_options` as `settings.0.multiple_options`}
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={field.value as boolean}
                      onChange={field.onChange}
                      disabled={disabled}
                      size="small"
                    />
                  }
                  label={t('multi_select_options_label')}
                  labelPlacement="start"
                  sx={{ mr: 0 }}
                />
              )}
            />
          </Box>

          <OptionsEditor settingIndex={index} disabled={disabled} />
        </>
      )}

      {settingType === 'text' && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
            {t('max_length_label')}
          </Typography>
          <Controller
            // Dynamic path cast - safe since we check settingType === 'text'
            name={`settings.${index}.max_length` as `settings.0.max_length`}
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                value={field.value}
                onChange={(e) => {
                  field.onChange(Number(e.target.value))
                }}
                type="number"
                size="small"
                placeholder="500"
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                disabled={disabled}
                sx={{ width: 150, ...styles.textField }}
              />
            )}
          />
        </Box>
      )}
    </Paper>
  )
}

type SortableSettingRowProps = {
  id: string
  children: React.ReactElement
}

const SortableSettingRow = ({ id, children }: SortableSettingRowProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  })

  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
    zIndex: isDragging ? 1 : undefined,
    position: isDragging ? 'relative' : undefined,
  }

  return (
    <Box ref={setNodeRef} style={style}>
      {React.cloneElement(children, {
        dragHandleProps: { ...attributes, ...listeners },
        isDragging,
      })}
    </Box>
  )
}

type ExternalMetadataConfigFormProps = {
  initialData?: ExternalMetadataConfigOutput | null
  onSubmit: (data: ConfigFormValues) => Promise<void>
  onCancel: () => void
  isLoading?: boolean
}

export const ExternalMetadataConfigForm = ({
  initialData,
  onSubmit,
  onCancel,
  isLoading,
}: ExternalMetadataConfigFormProps) => {
  const { t } = useTranslation('common', { keyPrefix: 'admin.external_metadata' })
  const { t: tCommon } = useTranslation('common')
  // Menu state for "Add Group" dropdown
  const [addMenuAnchor, setAddMenuAnchor] = useState<null | HTMLElement>(null)

  const form = useForm<ConfigFormValues>({
    resolver: zodResolver(createConfigFormSchema(t)),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: initialData
      ? {
          name: initialData.name,
          settings: normalizeSettings(initialData.settings),
        }
      : {
          name: '',
          settings: [],
        },
  })

  const { fields, append, remove, move } = useFieldArray({
    control: form.control,
    name: 'settings',
  })

  // Reset form when initialData changes (e.g., after migration)
  useEffect(() => {
    form.reset(
      initialData
        ? {
            name: initialData.name,
            settings: normalizeSettings(initialData.settings),
          }
        : {
            name: '',
            settings: [],
          },
    )
  }, [initialData, form])

  const { isDirty } = form.formState

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const oldIndex = fields.findIndex((f) => f.id === active.id)
    const newIndex = fields.findIndex((f) => f.id === over.id)

    if (oldIndex !== -1 && newIndex !== -1) {
      move(oldIndex, newIndex)
    }
  }

  const handleSubmit = form.handleSubmit(async (data) => {
    await onSubmit(data)
  })

  return (
    <FormProvider {...form}>
      <Box component="form" onSubmit={handleSubmit}>
        {/* Config Name */}
        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
          {t('configuration_name')}
        </Typography>
        <Controller
          name="name"
          control={form.control}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              fullWidth
              size="small"
              placeholder={t('configuration_name_placeholder')}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              disabled={isLoading}
              inputProps={{ maxLength: MAX_LENGTHS.configName }}
              sx={{ mb: 3, ...styles.textField }}
            />
          )}
        />

        {/* Custom Metadata Section */}
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
            {t('custom_metadata')}
          </Typography>

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            modifiers={[restrictToVerticalAxis, restrictToWindowEdges]}
          >
            <SortableContext items={fields.map((f) => f.id)} strategy={verticalListSortingStrategy}>
              {fields.map((field, index) => (
                <SortableSettingRow key={field.id} id={field.id}>
                  <SettingRow index={index} onRemove={() => remove(index)} disabled={isLoading} />
                </SortableSettingRow>
              ))}
            </SortableContext>
          </DndContext>

          {fields.length === 0 && (
            <Box
              sx={{
                py: 4,
                px: 2,
                textAlign: 'center',
                bgcolor: 'grey.50',
                borderRadius: 1,
                mb: 2,
              }}
            >
              <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                {t('no_metadata_yet')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('add_fields_help_text')}
                <br />
                {t('field_type_help_text')}
              </Typography>
            </Box>
          )}

          {/* Create Metadata section */}
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box>
              <Typography variant="subtitle2">{t('create_metadata_title')}</Typography>
              <Typography variant="body2" color="text.secondary">
                {t('create_metadata_description')}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<Plus size={16} />}
              onClick={(e) => setAddMenuAnchor(e.currentTarget)}
              disabled={isLoading}
            >
              {t('add_field_button')}
            </Button>
            <Menu
              anchorEl={addMenuAnchor}
              open={Boolean(addMenuAnchor)}
              onClose={() => setAddMenuAnchor(null)}
            >
              <MenuItem
                onClick={() => {
                  append(getDefaultSetting('options'))
                  setAddMenuAnchor(null)
                }}
              >
                {t('options_dropdown_menu_item')}
              </MenuItem>
              <MenuItem
                onClick={() => {
                  append(getDefaultSetting('text'))
                  setAddMenuAnchor(null)
                }}
              >
                {t('text_free_input_menu_item')}
              </MenuItem>
            </Menu>
          </Paper>
        </Paper>

        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Button onClick={onCancel} disabled={isLoading} variant="outlined">
            {tCommon('actions.cancel')}
          </Button>
          <LoadingButton
            type="submit"
            variant="contained"
            loading={isLoading}
            disabled={!isDirty || isLoading}
          >
            {t('save_metadata_button')}
          </LoadingButton>
        </Box>
      </Box>
    </FormProvider>
  )
}
